/**
 * Right-side slide-in drawer for inspecting a single TG account.
 *
 * Sections:
 *   1. Header — phone + display name + dismiss
 *   2. Profile card — TG identity (id/username/first/last), refresh button
 *   3. Lifecycle card — status / warmupPhase / health / dailyDm / proxy
 *   4. Groups card — groups this account joined; inline "join via link" form
 *      + leave button per row. Refetches groups query after either op.
 *
 * All actions go through the existing /api/admin endpoints; drawer holds
 * no local state beyond form inputs.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AccountSummary,
  type AddGroupsResponse,
  type GroupSummary,
  type ProxySummary,
  type RefreshAccountProfileResponse,
  Routes,
} from '@tg-leads/contracts';

import { api, ApiError } from '../lib/api';

interface GroupList {
  items: GroupSummary[];
  nextCursor: string | null;
}
interface ProxyList {
  items: ProxySummary[];
  nextCursor: string | null;
}

export function AccountDetailDrawer({
  account,
  onClose,
}: {
  account: AccountSummary;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const proxies = useQuery({
    queryKey: ['proxies'],
    queryFn: () => api.get<ProxyList>(Routes.admin.proxies),
  });
  const proxy = account.proxyId
    ? proxies.data?.items.find((p) => p.id === account.proxyId)
    : null;

  const refresh = useMutation({
    mutationFn: () =>
      api.post<RefreshAccountProfileResponse>(
        Routes.admin.accountRefreshProfile(account.id),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      // Refresh now also pulls iterDialogs and upserts groups; invalidate
      // both the drawer's "已加入的群" list and the standalone GroupsPage.
      qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="absolute right-0 top-0 h-full w-[520px] bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <div className="text-lg font-semibold">
                {displayName(account) || account.phone}
              </div>
              <div className="text-sm text-slate-500 font-mono">
                {account.phone}
              </div>
            </div>
            <button
              className="text-slate-400 hover:text-slate-700 text-xl leading-none"
              onClick={onClose}
              aria-label="关闭"
            >
              ×
            </button>
          </div>

          {/* Status-based warning banners */}
          {account.status === 'banned' && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded p-3">
              <div className="font-medium mb-1">⚠️ 会话凭据已失效（已封禁）</div>
              <div>
                TG 返回了 AUTH_KEY_UNREGISTERED / FROZEN_METHOD_INVALID。这个账号需要重新登录：
                删除后重新导入新会话字符串，或走短信登录。
              </div>
            </div>
          )}
          {account.status === 'restricted' && (
            <div className="text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded p-3">
              <div className="font-medium mb-1">⚠️ 账号被限频（PEER_FLOOD）</div>
              <div>
                TG 限制了发送行为，一般 12–24 小时自动恢复。建议先把养号阶段调回"冷启动"观察。
              </div>
            </div>
          )}
          {account.status === 'active' && !account.tgProfileFetchedAt && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 rounded p-3">
              <div className="font-medium mb-1">⚠️ 还没拉到 TG 资料</div>
              <div>
                账号已上线但 getMe 还没成功 — 等几秒重试，或点右侧"从 TG 刷新"。
                如果一直拉不到，可能是会话凭据有问题。
              </div>
            </div>
          )}

          {/* TG profile */}
          <section className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-700">TG 资料</h3>
              <button
                className="btn-secondary text-xs"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending || account.status !== 'active'}
                title={
                  account.status !== 'active'
                    ? '账号未连接时无法刷新（需要状态"启用中"且养号阶段"就绪"）'
                    : ''
                }
              >
                {refresh.isPending ? '拉取中…' : '从 TG 刷新'}
              </button>
            </div>
            <Fields
              rows={[
                ['TG 用户 ID', account.tgUserId ?? '—'],
                ['TG 用户名', account.tgUsername ? `@${account.tgUsername}` : '—'],
                ['名（first name）', account.tgFirstName ?? '—'],
                ['姓（last name）', account.tgLastName ?? '—'],
                [
                  '最近拉取',
                  account.tgProfileFetchedAt
                    ? new Date(account.tgProfileFetchedAt).toLocaleString()
                    : '从未',
                ],
              ]}
            />
            {refresh.error && (
              <ErrorBox err={refresh.error} />
            )}
          </section>

          {/* Lifecycle */}
          <section className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-700">运行状态</h3>
              <WarmupSelect account={account} />
            </div>
            <Fields
              rows={[
                ['用途', purposeLabel(account.purpose)],
                ['状态', statusLabel(account.status)],
                ['连接', account.isConnected ? '在线（mtcute 已连接）' : '离线（未连接 / 已掉线）'],
                ['健康分', String(account.healthScore)],
                ['账号年龄（天）', String(account.ageDays)],
                ['今日已发私信', String(account.dailyDmCount)],
                [
                  '代理',
                  proxy
                    ? `${proxy.endpoint}${proxy.hasAuth ? ' 🔒' : ''}`
                    : account.proxyId
                      ? account.proxyId.slice(0, 8)
                      : '—',
                ],
                ['创建时间', new Date(account.createdAt).toLocaleString()],
              ]}
            />
          </section>

          {/* TG official PMs (login codes / security alerts) */}
          <AccountSystemMessagesCard accountId={account.id} />

          {/* Groups */}
          <AccountGroupsCard accountId={account.id} />
        </div>
      </aside>
    </div>
  );
}

/**
 * Shows the latest PMs from TG service accounts (777000 etc.) addressed
 * to this account. Phase 1: raw text only — admin reads to find login
 * code when they need to sign this account in elsewhere (e.g. PC client
 * after losing phone number). Auto-refreshes every 10s while drawer open.
 */
function AccountSystemMessagesCard({ accountId }: { accountId: string }) {
  const msgs = useQuery({
    queryKey: ['accountSystemMessages', accountId],
    queryFn: () =>
      api.get<{ items: Array<{
        id: string;
        senderUserId: string;
        tgMessageId: string;
        text: string;
        parsedLoginCode: string | null;
        receivedAt: string;
      }> }>(Routes.admin.accountSystemMessages(accountId)),
    refetchInterval: 10_000,
  });

  const items = msgs.data?.items ?? [];

  return (
    <section className="border rounded-lg p-4 space-y-3">
      <div>
        <h3 className="font-medium text-slate-700">TG 官方消息（最近 20 条）</h3>
        <p className="text-xs text-slate-500 mt-1">
          捕获来自 TG 官方账号（sender id 777000 / 42777）的私信，包括登录验证码、
          新设备登录通知等。每 10 秒自动刷新。
          <strong className="text-amber-700">登录验证码 5 分钟内有效，及时使用。</strong>
        </p>
      </div>
      {msgs.isLoading ? (
        <div className="text-sm text-slate-500">加载中…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500">
          还没收到任何 TG 官方消息。在别的设备触发一次登录，验证码就会出现在这里。
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((m) => (
            <SystemMessageItem key={m.id} m={m} />
          ))}
        </ul>
      )}
    </section>
  );
}

function SystemMessageItem({
  m,
}: {
  m: {
    id: string;
    senderUserId: string;
    tgMessageId: string;
    text: string;
    parsedLoginCode: string | null;
    receivedAt: string;
  };
}) {
  const receivedMs = new Date(m.receivedAt).getTime();
  const ageSec = Math.max(0, Math.floor((Date.now() - receivedMs) / 1000));
  // Login codes expire ~5 min. After that, show as expired (but keep
  // the code visible — admin might still want to copy for audit).
  const expired = ageSec > 300;
  return (
    <li className="border-l-2 border-indigo-400 pl-3 py-1">
      {m.parsedLoginCode && (
        <div
          className={`mb-2 flex items-center gap-3 rounded px-3 py-2 ${
            expired
              ? 'bg-slate-100 border border-slate-200'
              : 'bg-emerald-50 border border-emerald-200'
          }`}
        >
          <span className="text-xs text-slate-500">🔑 验证码</span>
          <span
            className={`text-2xl font-mono font-bold tracking-widest ${
              expired ? 'text-slate-400 line-through' : 'text-emerald-800'
            }`}
          >
            {m.parsedLoginCode}
          </span>
          <button
            className="btn-primary text-xs ml-auto"
            disabled={expired}
            onClick={() => {
              void navigator.clipboard.writeText(m.parsedLoginCode!);
            }}
          >
            {expired ? '已过期' : '复制验证码'}
          </button>
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
        <span className="font-mono">
          发件人 ID {m.senderUserId} · 消息 ID {m.tgMessageId}
        </span>
        <span title={new Date(m.receivedAt).toLocaleString()}>
          收到于 {formatAge(ageSec)}
        </span>
      </div>
      <div className="text-sm whitespace-pre-wrap break-words bg-slate-50 p-2 rounded font-mono text-xs">
        {m.text}
      </div>
      <div className="mt-1 flex justify-end">
        <button
          className="btn-secondary text-[10px]"
          onClick={() => {
            void navigator.clipboard.writeText(m.text);
          }}
        >
          复制全文
        </button>
      </div>
    </li>
  );
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} 秒前`;
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  return `${Math.floor(hr / 24)} 天前`;
}

function purposeLabel(p: AccountSummary['purpose']): string {
  return p === 'sender' ? '外联号' : '采集号';
}

function statusLabel(s: AccountSummary['status']): string {
  switch (s) {
    case 'active':
      return '启用中';
    case 'restricted':
      return '受限';
    case 'cooling':
      return '冷却中';
    case 'banned':
      return '已封禁';
    default:
      return s;
  }
}

function groupStatusLabel(s: GroupSummary['status']): string {
  switch (s) {
    case 'active':
      return '已加入';
    case 'pending':
      return '加群中';
    case 'readonly':
      return '只读';
    case 'left':
      return '已退出';
    case 'kicked':
      return '被踢出';
    default:
      return s;
  }
}

function AccountGroupsCard({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [link, setLink] = useState('');

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupList>(Routes.admin.groups),
  });

  const mine = useMemo(
    () =>
      (groups.data?.items ?? []).filter(
        (g) => g.joinedByAccountId === accountId,
      ),
    [groups.data, accountId],
  );

  const joinMut = useMutation({
    mutationFn: () =>
      api.post<AddGroupsResponse>(Routes.admin.groups, {
        links: [link.trim()],
        joinByAccountId: accountId,
      }),
    onSuccess: () => {
      setLink('');
      qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });

  const leaveMut = useMutation({
    mutationFn: (groupId: string) =>
      api.post<GroupSummary>(Routes.admin.groupLeave(groupId), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });

  return (
    <section className="border rounded-lg p-4 space-y-3">
      <h3 className="font-medium text-slate-700">该账号已加入的群</h3>

      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          joinMut.mutate();
        }}
        className="flex gap-2"
      >
        <input
          className="input flex-1"
          placeholder="https://t.me/xxx 或 @用户名"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={joinMut.isPending || !link.trim()}
        >
          {joinMut.isPending ? '加群中…' : '加入'}
        </button>
      </form>
      {joinMut.error && <ErrorBox err={joinMut.error} />}
      {joinMut.data?.failures.length ? (
        <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">
          {joinMut.data.failures.map((f) => `${f.link}: ${f.reason}`).join('；')}
        </div>
      ) : null}

      {groups.isLoading ? (
        <div className="text-sm text-slate-500">加载中…</div>
      ) : !mine.length ? (
        <div className="text-sm text-slate-500">这个账号还没加入任何群。</div>
      ) : (
        <ul className="divide-y border rounded-md">
          {mine.map((g) => (
            <li key={g.id} className="px-3 py-2 flex items-center justify-between text-sm">
              <div className="min-w-0 flex-1 mr-2">
                <div className="truncate font-medium">{g.title}</div>
                <div className="text-xs text-slate-500 font-mono">{g.tgChatId}</div>
              </div>
              <span className={`badge-${badgeKind(g.status)} mr-2`}>{groupStatusLabel(g.status)}</span>
              <button
                className="btn-danger text-xs"
                onClick={() => {
                  if (confirm(`让该账号退出 "${g.title}"?`)) leaveMut.mutate(g.id);
                }}
                disabled={
                  leaveMut.isPending || g.status === 'left' || g.status === 'kicked'
                }
              >
                退群
              </button>
            </li>
          ))}
        </ul>
      )}
      {leaveMut.error && <ErrorBox err={leaveMut.error} />}
    </section>
  );
}

function Fields({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="grid grid-cols-3 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-slate-500">{k}</dt>
          <dd className="col-span-2 font-mono break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function ErrorBox({ err }: { err: unknown }) {
  const msg = err instanceof ApiError ? err.message : String(err);
  return (
    <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">{msg}</div>
  );
}

function displayName(a: AccountSummary): string {
  const parts = [a.tgFirstName, a.tgLastName].filter(Boolean).join(' ');
  if (parts) return a.tgUsername ? `${parts} (@${a.tgUsername})` : parts;
  return a.tgUsername ? `@${a.tgUsername}` : '';
}

function badgeKind(status: GroupSummary['status']): 'ok' | 'warn' | 'fail' | 'neutral' {
  switch (status) {
    case 'active':
      return 'ok';
    case 'pending':
    case 'readonly':
      return 'warn';
    case 'kicked':
      return 'fail';
    case 'left':
    default:
      return 'neutral';
  }
}

function WarmupSelect({ account }: { account: AccountSummary }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (warmupPhase: 'cold' | 'warming' | 'ready') =>
      api.put(Routes.admin.accountWarmup(account.id), { warmupPhase }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
  return (
    <select
      className="text-xs border border-slate-300 rounded px-2 py-1"
      value={account.warmupPhase}
      disabled={mut.isPending}
      onChange={(e) =>
        mut.mutate(e.target.value as 'cold' | 'warming' | 'ready')
      }
      title="切换养号阶段 — 改成『就绪』后调度器会自动把账号上线"
    >
      <option value="cold">冷启动</option>
      <option value="warming">养号中</option>
      <option value="ready">就绪</option>
    </select>
  );
}
